import { normalizeHost } from '@/lib/brands/identity'

export const ENGINES = ['chatgpt', 'perplexity', 'gemini', 'google_aio'] as const

/**
 * AI assistants, answer engines, and LLM products that must NEVER be returned
 * as detected competitors. These are infrastructure, not market competitors.
 *
 * Used in two places:
 *   1. The extraction prompt — instructs the model to omit these.
 *   2. A post-filter in extractBrandsWithLLM — defensive guard in case the
 *      model ignores the instruction.
 *
 * Matching is case-insensitive and normalized (lowercased, trimmed).
 */
export const AI_ENGINE_BRAND_EXCLUSIONS: readonly string[] = [
  'ChatGPT',
  'OpenAI',
  'GPT',
  'Perplexity',
  'Gemini',
  'Google',
  'Bard',
  'Google AI Overview',
  'Claude',
  'Anthropic',
  'Copilot',
  'Microsoft Copilot',
  'Bing',
  'Grok',
  'DeepSeek',
  'Llama',
  'Meta AI',
  'You.com',
  'Poe',
]

/**
 * Lowercased, trimmed version of AI_ENGINE_BRAND_EXCLUSIONS for fast O(1) lookup.
 */
export const AI_ENGINE_BRAND_EXCLUSIONS_SET: ReadonlySet<string> = new Set(
  AI_ENGINE_BRAND_EXCLUSIONS.map((e) => e.toLowerCase().trim())
)

/**
 * High-signal engine tokens used for word-boundary matching in the post-filter.
 *
 * Each token is matched as a whole word (surrounded by non-word characters or
 * string boundaries) against the lowercased, trimmed brand name. This catches
 * compound variants like "Google Gemini", "Microsoft Copilot", "GPTBot", or
 * "Google AI Overviews" that slip past the exact-match set.
 *
 * Conservative by design — tokens are chosen to be unambiguous standalone words
 * that only appear in AI engine product names, not in legitimate brand names.
 * Notably absent: "google" (too many legitimate products like Google Forms,
 * Google Analytics clients), "bing" (appears mid-word in unrelated names),
 * "ai" (too generic). Over-match is worse than under-match here.
 */
export const AI_ENGINE_TOKEN_EXCLUSIONS: readonly string[] = [
  'chatgpt',
  'gpt',
  'gemini',
  'copilot',
  'perplexity',
  'claude',
  'gptbot',
  'bingbot',
  'ai overview',
  'ai overviews',
  'meta ai',
  'bing chat',
  'you.com',
  'deepseek',
  'grok',
]

/** One trailing top-level domain: ".com", ".ai", ".io". */
const TRAILING_TLD = /\.[a-z]{2,24}$/

/**
 * The hosts of the assistants named in AI_ENGINE_BRAND_EXCLUSIONS — keep the two lists in step:
 * a new assistant name is added there and its host here. A candidate whose host equals a listed
 * host, or is a subdomain of one at a label boundary ("chat.openai.com" via "openai.com"), is an
 * assistant. Nothing else is excluded by host: google.com, bing.com, microsoft.com, meta.com and
 * their other subdomains and paths ("search.google.com", "google.com/business") are real products.
 */
const AI_ENGINE_HOSTS: readonly string[] = [
  'chatgpt.com',
  'openai.com',
  'claude.ai',
  'anthropic.com',
  'perplexity.ai',
  'gemini.google.com',
  'bard.google.com',
  'copilot.microsoft.com',
  'copilot.cloud.microsoft',
  'meta.ai',
  'x.ai',
  'grok.com',
  'deepseek.com',
  'poe.com',
  'you.com',
]

/** A name with its spaces and dots removed: "Meta AI", "meta.ai" and "You.com" fold to one form. */
const compactName = (s: string): string => s.toLowerCase().replace(/[\s.]+/g, '')

const AI_ENGINE_COMPACT_SET: ReadonlySet<string> = new Set(
  AI_ENGINE_BRAND_EXCLUSIONS.map(compactName)
)

/**
 * True when the value's host is a listed assistant host or a subdomain of one. A value that is
 * not a host (a brand name with a space, "Chat", "co.uk") has no host and never matches.
 */
function isAssistantHost(value: string): boolean {
  if (/\s/.test(value.trim())) return false
  const host = normalizeHost(value)
  if (!host) return false
  return AI_ENGINE_HOSTS.some((listed) => host === listed || host.endsWith(`.${listed}`))
}

/**
 * Returns true when the given brand name should be excluded as an AI engine /
 * answer-engine product. Combines:
 *   1. Exact-match against AI_ENGINE_BRAND_EXCLUSIONS_SET (fast path), also when the
 *      name differs from an exact entry only by one trailing TLD ("OpenAI.com").
 *   2. Word-boundary token match against AI_ENGINE_TOKEN_EXCLUSIONS (catches
 *      compound variants like "Google Gemini" or "Microsoft Copilot").
 *   3. The host: `domain` when the candidate carries one, and `name` when it is itself a host
 *      ("meta.ai", "bard.google.com") — only when it is, or is under, an AI_ENGINE_HOSTS entry.
 *      A candidate from Google competitor data is named by its first label ("meta.ai" -> "Meta"),
 *      which no name check can catch.
 *
 * Exported for unit-testing; use this instead of calling
 * AI_ENGINE_BRAND_EXCLUSIONS_SET.has() directly.
 */
export function isExcludedAiEngine(name: string, domain?: string | null): boolean {
  const normalized = name.toLowerCase().trim()

  // Fast path: exact match
  if (AI_ENGINE_BRAND_EXCLUSIONS_SET.has(normalized)) return true

  // Same name spelled with other spaces or dots: "Meta.ai", "Open AI".
  if (AI_ENGINE_COMPACT_SET.has(compactName(normalized))) return true

  if (isAssistantHost(normalized)) return true
  if (domain && isAssistantHost(domain)) return true

  // An exact entry spelled with one trailing TLD ("OpenAI.com", "Poe.com", "Google.com",
  // "Bing.com"): the TLD-stripped name against the exact set, nothing looser.
  const withoutTld = normalized.replace(TRAILING_TLD, '')
  if (withoutTld !== normalized && AI_ENGINE_BRAND_EXCLUSIONS_SET.has(withoutTld)) return true

  // Word-boundary token match for compound variants.
  // We build a regex for each token at call time. The set is small (~12 entries)
  // and this function is called once per detected brand per engine result, so
  // the overhead is negligible. Caching is not worth the complexity.
  for (const token of AI_ENGINE_TOKEN_EXCLUSIONS) {
    // Escape any regex metacharacters in the token (e.g. the space in "ai overview")
    const escaped = token.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    // \b matches between a word char and a non-word char. Space is a non-word char,
    // so "ai overview" matches as two words joined by a space — the regex boundary
    // at the start/end of the full token string handles the outer edges.
    const re = new RegExp(`(?<![a-z0-9])${escaped}(?![a-z0-9])`, 'i')
    if (re.test(normalized)) return true
  }

  return false
}

export type Engine = (typeof ENGINES)[number]

/**
 * Engines the product currently OFFERS (selectable + marketed) = base + any
 * paid add-on. UI defaults ("engines a user could have") iterate this, NOT the
 * raw `ENGINES` enum — `ENGINES` still carries the dormant `gemini` engine so
 * historical runs render.
 *
 * These live here, not in `billing/constants.ts`, because that module
 * value-imports `serverEnv` and so cannot be reached from a Client Component
 * (ARC-01). This file has no imports at all, so onboarding screens and any
 * other client surface can read the offered list without hand-rolling one —
 * a hardcoded engine-name list is exactly what ARC-15 forbids.
 * `billing/constants.ts` re-exports all three, so existing importers are
 * unaffected.
 */
export const BASE_ENGINES: readonly Engine[] = ['chatgpt', 'perplexity', 'google_aio'] as const

// No paid engine add-ons today (Gemini is disabled; AI Mode is a planned future
// add-on). The add-on machinery is kept DORMANT for when one returns — see
// `billing/constants.ts` for the full inventory of what stays wired.
export const ADDON_ENGINES: readonly Engine[] = [] as const

/** Today `OFFERED_ENGINES` === `BASE_ENGINES`; stays correct when add-ons return. */
export const OFFERED_ENGINES: readonly Engine[] = [...BASE_ENGINES, ...ADDON_ENGINES] as const

/**
 * The AI assistants this project asks: its selected engines that are offered, in `OFFERED_ENGINES`
 * order. An empty, null or missing list gives `BASE_ENGINES`, the set a scheduled scan uses when
 * none is chosen (`cron/scan/route.ts`). The one reader behind every "N assistants" figure: the
 * Overview header and caveat, and the Run scan dialog (simpler-surfaces AC-7).
 */
export function projectAssistantEngines(selected: readonly string[] | null | undefined): Engine[] {
  const chosen = OFFERED_ENGINES.filter((engine) => selected?.includes(engine))
  return chosen.length > 0 ? chosen : [...BASE_ENGINES]
}

export const ENGINE_LABELS: Record<Engine, string> = {
  chatgpt: 'ChatGPT',
  perplexity: 'Perplexity',
  gemini: 'Gemini',
  google_aio: 'Google AI Overview',
}

/**
 * Narrow a list of raw engine strings (e.g. from the DB) to the known `Engine`
 * union, dropping anything unrecognized. Use before rendering `EngineLogo` —
 * an unknown string resolves to an undefined logo src and breaks the image.
 */
export function toKnownEngines(engines: readonly string[]): Engine[] {
  return engines.filter((e): e is Engine => (ENGINES as readonly string[]).includes(e))
}
